import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../api';
import './OffboardingModal.css';

// step: 'confirm' | 'running' | 'done'
function OffboardingModal({ person, onClose, onDone }) {
  const { t } = useTranslation();
  const STATUS_META = {
    REVOKED:  { label: t('OffboardingModal.statusRevoked'),  icon: 'fa-check-circle', color: '#16a34a' },
    ACTIVE:   { label: t('OffboardingModal.statusActive'),   icon: 'fa-exclamation-circle', color: '#dc2626' },
    UNKNOWN:  { label: t('OffboardingModal.statusUnknown'),  icon: 'fa-question-circle', color: '#94a3b8' },
  };
  const [step,   setStep]   = useState('confirm');
  const [checks, setChecks] = useState([]);
  const [error,  setError]  = useState(null);

  const handleOffboard = async () => {
    setStep('running');
    setError(null);
    try {
      const res = await axiosInstance.post(`/people/${person.id}/offboard`);
      setChecks(res.data.systemChecks || []);
      setStep('done');
    } catch {
      setError(t('OffboardingModal.errorOffboardingFailed'));
      setStep('confirm');
    }
  };

  const handleDone = () => {
    onDone(person.id);
    onClose();
  };

  return (
    <div className="ob-overlay" onClick={step === 'confirm' ? onClose : undefined}>
      <div className="ob-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="ob-header">
          <div className="ob-header-left">
            <div className="ob-avatar">{(person.displayName || '?')[0].toUpperCase()}</div>
            <div>
              <div className="ob-person-name">{person.displayName}</div>
              <div className="ob-person-email">{person.email}</div>
            </div>
          </div>
          {step === 'confirm' && (
            <button className="ob-close" onClick={onClose}><i className="fas fa-times" /></button>
          )}
        </div>

        {/* Confirm step */}
        {step === 'confirm' && (
          <>
            <div className="ob-warning">
              <i className="fas fa-exclamation-triangle ob-warn-icon" />
              <div>
                <strong>{t('OffboardingModal.confirmHeading')}</strong>
                <p>{t('OffboardingModal.confirmBody')}</p>
              </div>
            </div>
            {error && <div className="ob-error"><i className="fas fa-times-circle" /> {error}</div>}
            <div className="ob-preview-checks">
              <div className="ob-preview-label">{t('OffboardingModal.systemsToBeChecked')}</div>
              <div className="ob-preview-list">
                {['Azure AD', 'GitHub', 'AWS IAM'].map(s => (
                  <span key={s} className="ob-preview-chip">{s}</span>
                ))}
              </div>
            </div>
            <div className="ob-actions">
              <button className="ob-btn-cancel" onClick={onClose}>{t('OffboardingModal.cancel')}</button>
              <button className="ob-btn-offboard" onClick={handleOffboard}>
                <i className="fas fa-user-slash" /> {t('OffboardingModal.offboardAndRevokeAccess')}
              </button>
            </div>
          </>
        )}

        {/* Running step */}
        {step === 'running' && (
          <div className="ob-running">
            <div className="ob-spinner" />
            <p className="ob-running-text">{t('OffboardingModal.revokingAccess')}</p>
          </div>
        )}

        {/* Done step */}
        {step === 'done' && (
          <div className="ob-done">
            <div className="ob-done-badge">
              <i className="fas fa-user-slash" />
            </div>
            <h3 className="ob-done-title">{t('OffboardingModal.offboardingComplete')}</h3>
            <p className="ob-done-sub">{t('OffboardingModal.checksRunForPrefix')} <strong>{person.displayName}</strong>{t('OffboardingModal.checksRunForSuffix')}</p>

            <div className="ob-checklist">
              {checks.map((c, i) => {
                const meta = STATUS_META[c.status] || STATUS_META.UNKNOWN;
                return (
                  <div key={i} className="ob-check-row">
                    <div className="ob-check-system">
                      <i className={`fab ${c.icon} ob-sys-icon`} style={{ color: c.color }} />
                      <span className="ob-sys-name">{c.system}</span>
                    </div>
                    <div className="ob-check-status">
                      <i className={`fas ${meta.icon}`} style={{ color: meta.color, marginRight: 5 }} />
                      <span style={{ color: meta.color, fontWeight: 600, fontSize: 13 }}>{meta.label}</span>
                    </div>
                    {c.detail && <div className="ob-check-detail">{c.detail}</div>}
                  </div>
                );
              })}
            </div>

            {checks.some(c => c.status === 'ACTIVE') && (
              <div className="ob-action-needed">
                <i className="fas fa-exclamation-circle" />
                <span>{t('OffboardingModal.manualActionRequired')}</span>
              </div>
            )}

            <button className="ob-btn-done" onClick={handleDone}>
              <i className="fas fa-check" /> {t('OffboardingModal.done')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default OffboardingModal;
